import toast from "react-hot-toast";

import { Icon } from "@/design-system";

/**
 * The reference's toast (grep `============ TOAST` in
 * `Dens N Dente Tailwind.dc.html`), served through the existing
 * `ToastNotification` API so the ~50 call sites keep working unchanged.
 *
 * Anatomy — one dark card: `bg-neutral-900`, a 30px status circle (Material
 * Symbols glyph, never an emoji), a bold `text-sm` title with an optional
 * `text-xs text-neutral-300` subtitle under it, and optional text-button
 * actions on the right. **No close ×** — the reference is auto-dismiss only.
 *
 * One at a time: every variant defaults to the same toast id, so a new toast
 * replaces whatever is showing — the reference's single `toast` state slot.
 * Pass `options.id` to opt out (the loading→update flow keeps its own id).
 *
 * Extended options over the old API (all optional, used by cartSlice):
 *   sub      — subtitle line ("Added to cart / <product>").
 *   action   — `{ label, onClick, muted }` or an array of them. `muted` renders
 *              the reference's grey Undo styling instead of bold white.
 *              Dismissal is the handler's business, exactly like the reference:
 *              Undo dismisses inside its handler, "View cart" leaves the toast
 *              to its timer.
 *   duration — reference paces by importance: 3200 default, 3400–3600 for
 *              cart-flavoured ones. error/warning keep the app's longer 4500.
 *
 * The reference fixture only ever shows confirmations, so it has no error or
 * warning variant. Those keep the same dark anatomy with the circle switched to
 * `--error-500` / `--orange-500` and a `priority_high` glyph — an extrapolation,
 * not upstream markup.
 */

const SINGLETON_ID = "dnd-toast";

const CIRCLE = {
  success: { background: "var(--success-500)", icon: "check" },
  error: { background: "var(--error-500)", icon: "priority_high" },
  warning: { background: "var(--orange-500)", icon: "priority_high" },
};

function StatusCircle({ variant }) {
  if (variant === "loading") {
    return (
      <span
        aria-hidden="true"
        className="inline-flex h-[30px] w-[30px] shrink-0 rounded-full"
        style={{
          border: "3px solid var(--neutral-600)",
          borderTopColor: "#fff",
          animation: "spin 1s linear infinite",
        }}
      />
    );
  }
  const { background, icon } = CIRCLE[variant] || CIRCLE.success;
  return (
    <span
      className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full"
      style={{ background }}
    >
      <Icon name={icon} size={18} fill className="text-white" />
    </span>
  );
}

function ToastCard({ t, variant, title, sub, actions }) {
  return (
    <div className={t.visible ? "dnd-toast-enter" : "dnd-toast-exit"}>
      <div className="flex min-w-[300px] items-center gap-[14px] rounded-md bg-neutral-900 px-[18px] py-[14px] font-sans text-white shadow-lg">
        <StatusCircle variant={variant} />
        <div className="flex-1">
          <div className="text-sm font-bold">{title}</div>
          {sub ? (
            <div className="max-w-[300px] text-xs text-neutral-300 [text-wrap:pretty]">
              {sub}
            </div>
          ) : null}
        </div>
        {actions.map(({ label, onClick, muted }) => (
          <button
            key={label}
            onClick={onClick}
            className={`shrink-0 cursor-pointer whitespace-nowrap border-0 bg-transparent p-0 font-sans text-sm ${
              muted ? "font-semibold text-neutral-300" : "font-bold text-white"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

function show(variant, title, options = {}) {
  const { autoClose, sub, action, duration, ...restOptions } = options;
  const actions = Array.isArray(action) ? action : action ? [action] : [];
  const fallback = variant === "success" ? 3200 : 4500;
  return toast.custom(
    (t) => (
      <ToastCard
        t={t}
        variant={variant}
        title={title}
        sub={sub}
        actions={actions}
      />
    ),
    {
      id: SINGLETON_ID,
      duration: autoClose === false ? Infinity : duration || fallback,
      ...restOptions,
    }
  );
}

class ToastNotification {
  static success(message, options = {}) {
    return show("success", message, options);
  }

  static error(message, options = {}) {
    return show("error", message, options);
  }

  static warning(message, options = {}) {
    return show("warning", message, options);
  }

  // Returns the toast id so callers can hand it to update() when the work lands.
  static loading(message = "Loading...", options = {}) {
    return show("loading", message, { duration: Infinity, ...options });
  }

  static update(toastId, { render, type, isLoading, ...options }) {
    return show(type === "error" ? "error" : "success", render, {
      id: toastId,
      ...options,
    });
  }

  static dismiss(toastId) {
    return toast.dismiss(toastId);
  }

  static dismissAll() {
    return toast.dismiss();
  }
}

export default ToastNotification;

// Individual exports for convenience
export const { success, error, warning, loading, update, dismiss, dismissAll } =
  ToastNotification;
